/**
 * Notification Hooks
 * Combined hook file for notification queries and mutations
 * Following project patterns for TanStack Query usage
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { queryKeys } from '@/utils/queryKeys';
import { NOTIFICATIONS, QUERY_CACHE } from '@/lib/constants';
import { useCrossTabSync } from './useCrossTabSync';
import * as notificationService from '@/services/notificationService';
import type { NotificationsResponse } from '@/services/notificationService';
import type { Notification } from '@/lib/notification-types';

/**
 * Query hook for fetching notifications with infinite scrolling (cursor-based pagination)
 */
export function useNotifications(limit: number = NOTIFICATIONS.PAGE_SIZE) {
  return useInfiniteQuery({
    queryKey: queryKeys.notifications.all(),
    queryFn: ({ pageParam }) => notificationService.getNotifications(limit, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: null as string | null,
    staleTime: NOTIFICATIONS.STALE_TIME,
    gcTime: QUERY_CACHE.GC_SHORT,
    throwOnError: true,
  });
}

/**
 * Helper hook that flattens paginated notifications for easy consumption
 */
export function useFlattenedNotifications(limit: number = NOTIFICATIONS.PAGE_SIZE) {
  const query = useNotifications(limit);
  
  const notifications = useMemo(
    () => query.data?.pages.flatMap(page => page.notifications) ?? [],
    [query.data?.pages]
  );
  
  return { ...query, notifications };
}

/**
 * Query hook for fetching unread notification count
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: queryKeys.notifications.count(),
    queryFn: notificationService.getUnreadNotificationCount,
    staleTime: NOTIFICATIONS.STALE_TIME,
    gcTime: QUERY_CACHE.GC_SHORT,
    throwOnError: true,
  });
}

/**
 * Mutation hooks with optimistic updates and cross-tab sync
 */
export function useNotificationMutations() {
  const queryClient = useQueryClient();
  const { broadcastBatchInvalidation } = useCrossTabSync();

  const markAsRead = useMutation({
    mutationFn: notificationService.markNotificationRead,
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all() });
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.count() });

      // Snapshot previous values
      const previousNotifications = queryClient.getQueryData<InfiniteData<NotificationsResponse>>(
        queryKeys.notifications.all()
      );
      const previousCount = queryClient.getQueryData<number>(
        queryKeys.notifications.count()
      );

      // Optimistic update - mark notification as read in paginated data
      queryClient.setQueryData<InfiniteData<NotificationsResponse>>(
        queryKeys.notifications.all(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              notifications: page.notifications.map(n =>
                n.id === notificationId
                  ? { ...n, read_at: new Date().toISOString() }
                  : n
              ),
            })),
          };
        }
      );

      // Optimistic update - decrement count
      queryClient.setQueryData<number>(
        queryKeys.notifications.count(),
        (old) => Math.max(0, (old ?? 1) - 1)
      );

      return { previousNotifications, previousCount };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          queryKeys.notifications.all(),
          context.previousNotifications
        );
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          queryKeys.notifications.count(),
          context.previousCount
        );
      }
    },
    onSuccess: () => {
      // Server refetch corrects any optimistic drift from blind decrements
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      broadcastBatchInvalidation(
        [[...queryKeys.notifications.all()], [...queryKeys.notifications.count()]],
        { mutationType: 'notification-read' }
      );
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: notificationService.markAllNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all() });
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.count() });

      const previousNotifications = queryClient.getQueryData<InfiniteData<NotificationsResponse>>(
        queryKeys.notifications.all()
      );
      const previousCount = queryClient.getQueryData<number>(
        queryKeys.notifications.count()
      );

      // Optimistic update - mark all as read in paginated data
      queryClient.setQueryData<InfiniteData<NotificationsResponse>>(
        queryKeys.notifications.all(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              notifications: page.notifications.map(n => ({
                ...n,
                read_at: n.read_at ?? new Date().toISOString(),
              })),
            })),
          };
        }
      );

      // Optimistic update - set count to 0
      queryClient.setQueryData<number>(queryKeys.notifications.count(), 0);

      return { previousNotifications, previousCount };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          queryKeys.notifications.all(),
          context.previousNotifications
        );
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          queryKeys.notifications.count(),
          context.previousCount
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      broadcastBatchInvalidation(
        [[...queryKeys.notifications.all()], [...queryKeys.notifications.count()]],
        { mutationType: 'notification-read-all' }
      );
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: notificationService.deleteNotification,
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all() });
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.count() });

      const previousNotifications = queryClient.getQueryData<InfiniteData<NotificationsResponse>>(
        queryKeys.notifications.all()
      );
      const previousCount = queryClient.getQueryData<number>(
        queryKeys.notifications.count()
      );

      // Get notification being deleted to check if unread
      const allNotifications = previousNotifications?.pages.flatMap(p => p.notifications) ?? [];
      const notificationToDelete = allNotifications.find(n => n.id === notificationId);
      const wasUnread = notificationToDelete && !notificationToDelete.read_at;

      // Optimistic update - remove from list in paginated data
      queryClient.setQueryData<InfiniteData<NotificationsResponse>>(
        queryKeys.notifications.all(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              notifications: page.notifications.filter(n => n.id !== notificationId),
            })),
          };
        }
      );

      // Optimistic update - decrement count if was unread
      if (wasUnread) {
        queryClient.setQueryData<number>(
          queryKeys.notifications.count(),
          (old) => Math.max(0, (old ?? 1) - 1)
        );
      }

      return { previousNotifications, previousCount };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          queryKeys.notifications.all(),
          context.previousNotifications
        );
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          queryKeys.notifications.count(),
          context.previousCount
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      broadcastBatchInvalidation(
        [[...queryKeys.notifications.all()], [...queryKeys.notifications.count()]],
        { mutationType: 'notification-delete' }
      );
    },
  });

  const markGroupAsReadMutation = useMutation({
    mutationFn: notificationService.markGroupNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      broadcastBatchInvalidation(
        [[...queryKeys.notifications.all()], [...queryKeys.notifications.count()]],
        { mutationType: 'notification-group-read' }
      );
    },
  });

  const notificationListViewedMutation = useMutation({
    mutationFn: notificationService.notificationListViewed,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.count() });
      const previousCount = queryClient.getQueryData<number>(queryKeys.notifications.count());
      queryClient.setQueryData(queryKeys.notifications.count(), 0);
      return { previousCount };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(queryKeys.notifications.count(), context.previousCount);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      broadcastBatchInvalidation(
        [[...queryKeys.notifications.count()]],
        { mutationType: 'notification-list-viewed' }
      );
    },
  });

  // Stabilize mutation refs to avoid infinite re-render loops
  // useMutation returns a new object each render, making useCallback deps unstable
  const markAsReadRef = useRef(markAsRead);
  markAsReadRef.current = markAsRead;
  const markAllAsReadRef = useRef(markAllAsRead);
  markAllAsReadRef.current = markAllAsRead;
  const markGroupAsReadRef = useRef(markGroupAsReadMutation);
  markGroupAsReadRef.current = markGroupAsReadMutation;
  const notificationListViewedRef = useRef(notificationListViewedMutation);
  notificationListViewedRef.current = notificationListViewedMutation;
  const deleteNotificationRef = useRef(deleteNotificationMutation);
  deleteNotificationRef.current = deleteNotificationMutation;

  return {
    markAsRead: useCallback(
      (notificationId: string) => markAsReadRef.current.mutateAsync(notificationId),
      []
    ),
    markAllAsRead: useCallback(
      () => markAllAsReadRef.current.mutateAsync(),
      []
    ),
    markGroupAsRead: useCallback(
      (params: { notificationType: string; entityId: string; referencePostId?: string }) =>
        markGroupAsReadRef.current.mutateAsync(params),
      []
    ),
    notificationListViewed: useCallback(
      () => notificationListViewedRef.current.mutateAsync(),
      []
    ),
    deleteNotification: useCallback(
      (notificationId: string) => deleteNotificationRef.current.mutateAsync(notificationId),
      []
    ),
    isMarkingAsRead: markAsRead.isPending,
    isMarkingAllAsRead: markAllAsRead.isPending,
    isDeleting: deleteNotificationMutation.isPending,
  };
}
